import { useState } from "react";
import { eur } from "@/lib/api";
import { Button } from "@/components/ui/button";
import ReviewPrompt from "@/components/client/ReviewPrompt";
import { Smartphone, HandCoins, ReceiptEuro, CheckCircle2, Loader2, Users, User, SplitSquareHorizontal, Gift, Mail } from "lucide-react";

export default function BillView({ bill, loading, t, theme, submitting, onPay, clientSessionId, restaurant, tableSessionId }) {
  const [mode, setMode] = useState("whole");
  const [selected, setSelected] = useState([]);
  const [shares, setShares] = useState(2);
  const [customAmt, setCustomAmt] = useState("");
  const [tipChoice, setTipChoice] = useState(0); // 0=none, 0.10/0.15/0.20, -1=custom
  const [tipCustom, setTipCustom] = useState("");
  const [email, setEmail] = useState("");

  if (loading || !bill) {
    return <div className="grid place-items-center py-24"><div className="w-8 h-8 rounded-full border-2 border-[#123026] border-t-transparent animate-spin" /></div>;
  }

  const total = bill.bill_total || 0;
  const paid = bill.paid_amount || 0;
  const remaining = bill.remaining || 0;
  const fullyPaid = bill.fully_paid;

  if (fullyPaid) {
    const reviewsOn = restaurant && restaurant.reviews_enabled !== false;
    return (
      <div data-testid="bill-paid" className="px-5 py-12 animate-slide-up">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-[#EBF2EE] grid place-items-center mx-auto mb-5">
            <CheckCircle2 className="w-10 h-10" style={{ color: theme.primary }} />
          </div>
          <h1 className="font-serif-display text-3xl text-[#123026]">{t.paidTitle}</h1>
          <p className="text-sm text-[#8A918D] mt-2 max-w-xs mx-auto">{t.paidText}</p>
        </div>
        {reviewsOn && (
          <div className="mt-8">
            <ReviewPrompt
              slug={restaurant.slug}
              tableSessionId={tableSessionId}
              clientSessionId={clientSessionId}
              googleUrl={restaurant.google_review_url}
              theme={theme}
              t={t}
            />
          </div>
        )}
        <p className="text-center text-[11px] text-[#B4B9B4] pt-10">{t.poweredBy}</p>
      </div>
    );
  }

  if (total <= 0) {
    return (
      <div data-testid="bill-view" className="px-5 py-16 text-center animate-slide-up">
        <div className="w-16 h-16 rounded-full bg-[#EBF2EE] grid place-items-center mx-auto mb-4">
          <ReceiptEuro className="w-8 h-8" style={{ color: theme.primary }} />
        </div>
        <h1 className="font-serif-display text-3xl text-[#123026] mb-3">{t.billTitle}</h1>
        <p data-testid="bill-empty" className="text-[#8A918D] text-sm">{t.billEmpty}</p>
      </div>
    );
  }

  const payingGuests = (bill.guests || []).filter((g) => (g.total || 0) > 0.01);
  // Multi-convives RÉEL : au moins 2 convives payants ET au moins 2 parts client identifiées.
  // Le nombre de scans QR seul ne compte pas (1 scan ≠ 1 personne).
  const identifiedMulti = payingGuests.length > 1 && (bill.client_shares || []).length > 1;
  const myShare = (bill.client_shares || []).find((c) => c.client_session_id === clientSessionId);
  const myAmount = myShare ? Math.round(myShare.amount * 100) / 100 : 0;
  const outstanding = bill.outstanding || remaining;
  // "Ma part" n'a de sens que si elle est réellement un sous-ensemble STRICT de l'addition.
  const showMine = identifiedMulti && myAmount > 0 && myAmount + 0.01 < outstanding;
  const covers = bill.covers || 0;
  // CAS 3 : plusieurs personnes SANS attribution individuelle (couverts réels > 1, ou plusieurs additions convives) -> jamais "ma part".
  const multiNoAttrib = !identifiedMulti && (covers > 1 || payingGuests.length > 1);
  // CAS 1 : un seul payeur / aucun contexte réel de partage -> uniquement "Payer X€".
  const singlePayer = !identifiedMulti && !multiNoAttrib;

  let amount, payMode, payAmount;
  if (mode === "mine" && showMine) { amount = myAmount; payMode = "amount"; payAmount = myAmount; }
  else if (mode === "divide") { const n = Math.max(2, shares || 2); amount = Math.round((outstanding / n) * 100) / 100; payMode = "amount"; payAmount = amount; }
  else if (mode === "amount") { amount = Math.round((parseFloat(customAmt) || 0) * 100) / 100; payMode = "amount"; payAmount = amount; }
  else { amount = outstanding; payMode = "whole"; payAmount = null; }
  amount = Math.round(amount * 100) / 100;

  const tipValue = tipChoice === -1
    ? Math.max(0, Math.round((parseFloat(tipCustom) || 0) * 100) / 100)
    : 0;
  const payTotal = Math.round((amount + tipValue) * 100) / 100;

  const pendingCash = Math.round((bill.pending_amount || 0) * 100) / 100;
  const cashRequested = pendingCash > 0 && pendingCash + 0.01 >= outstanding;
  const canPay = amount > 0 && amount <= outstanding + 0.01 && !submitting && !cashRequested;
  const doPay = (method) => onPay(method, { split_mode: payMode, amount: payAmount, guests: [], shares: 1, tip: tipValue, email: email.trim() || null });

  const statusBadge = (s) =>
    s === "paid" ? <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">Réglé</span>
      : s === "pending" ? <span className="text-[10px] font-bold text-[#C69C4A] bg-[#FAF3E0] px-1.5 py-0.5 rounded-full">Au serveur</span>
      : null;

  return (
    <div data-testid="bill-view" className="px-5 py-6 animate-slide-up">
      <div className="text-center mb-5">
        <div className="w-14 h-14 rounded-full bg-[#EBF2EE] grid place-items-center mx-auto mb-3">
          <ReceiptEuro className="w-7 h-7" style={{ color: theme.primary }} />
        </div>
        <h1 className="font-serif-display text-3xl text-[#123026]">{t.billTitle}</h1>
      </div>

      {/* Ledger summary */}
      <div className="rounded-2xl bg-white border border-[#EAE5DC] p-4 mb-5 space-y-1.5">
        <Row label={t.billTotal} value={eur(total)} />
        {paid > 0 && <Row label={t.paidSoFar} value={eur(paid)} className="text-emerald-600" />}
        <div className="flex justify-between items-center pt-2 mt-1 border-t border-[#EAE5DC]">
          <span className="font-semibold text-[#123026]">{t.remaining}</span>
          <span data-testid="bill-remaining" className="font-bold text-2xl text-[#123026]">{eur(remaining)}</span>
        </div>
      </div>

      {/* Guest list */}
      <div className="rounded-2xl bg-white border border-[#EAE5DC] p-4 mb-5">
        <p className="text-xs font-semibold uppercase tracking-wider text-[#8A918D] mb-2">{t.billItems}</p>
        {bill.guests.map((g, k) => (
          <div key={k} className="py-1.5 border-b border-[#F1EDE4] last:border-0">
            <div className="flex justify-between items-center">
              <span className="font-semibold text-[#123026] text-sm flex items-center gap-1.5">{g.guest_name} {statusBadge(g.status)}</span>
              <span className="font-semibold text-[#123026] text-sm">{eur(g.total)}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Adaptive payment selector — reflects the REAL state of the table, no redundant options */}
      <div className="mb-4">
        {!singlePayer && (<>
        <p className="text-xs font-semibold uppercase tracking-wider text-[#8A918D] mb-2">Comment souhaitez-vous régler ?</p>
        <div className="grid grid-cols-1 gap-2">
          <button data-testid="pay-mode-whole" onClick={() => setMode("whole")} className={`flex justify-between items-center rounded-xl px-4 py-3 text-sm font-semibold border transition-colors ${mode === "whole" ? "text-white border-transparent" : "bg-white text-[#123026] border-[#EAE5DC]"}`} style={mode === "whole" ? { backgroundColor: theme.primary } : {}}>
            <span className="flex items-center gap-2"><Users className="w-4 h-4" /> {identifiedMulti ? "Régler toute la table" : "Payer toute l'addition"}</span><span>{eur(outstanding)}</span>
          </button>

          {identifiedMulti && showMine && (
              <button data-testid="pay-mode-mine" onClick={() => setMode("mine")} className={`flex justify-between items-center rounded-xl px-4 py-3 text-sm font-semibold border transition-colors ${mode === "mine" ? "text-white border-transparent" : "bg-white text-[#123026] border-[#EAE5DC]"}`} style={mode === "mine" ? { backgroundColor: theme.primary } : {}}>
                <span className="flex items-center gap-2"><User className="w-4 h-4" /> Régler ma part</span><span>{eur(myAmount)}</span>
              </button>
          )}
          {multiNoAttrib && (
            <button data-testid="pay-mode-divide" onClick={() => setMode("divide")} className={`flex justify-between items-center rounded-xl px-4 py-3 text-sm font-semibold border transition-colors ${mode === "divide" ? "text-white border-transparent" : "bg-white text-[#123026] border-[#EAE5DC]"}`} style={mode === "divide" ? { backgroundColor: theme.primary } : {}}>
              <span className="flex items-center gap-2"><SplitSquareHorizontal className="w-4 h-4" /> Diviser l'addition</span>{mode === "divide" && <span>{eur(amount)}</span>}
            </button>
          )}

          <button data-testid="pay-mode-amount" onClick={() => setMode("amount")} className={`flex justify-between items-center rounded-xl px-4 py-3 text-sm font-semibold border transition-colors ${mode === "amount" ? "text-white border-transparent" : "bg-white text-[#123026] border-[#EAE5DC]"}`} style={mode === "amount" ? { backgroundColor: theme.primary } : {}}>
            <span className="flex items-center gap-2"><HandCoins className="w-4 h-4" /> Régler un montant</span>
          </button>
        </div>
        </>)}

        {mode === "divide" && (
          <div className="mt-2 rounded-xl border border-[#EAE5DC] p-3">
            <p className="text-xs text-[#8A918D] mb-2">Diviser en :</p>
            <div className="flex items-center gap-2 flex-wrap">
              {[2, 3, 4, 5, 6].map((n) => (
                <button key={n} data-testid={`divide-${n}`} onClick={() => setShares(n)} className={`w-11 h-11 rounded-full text-sm font-bold border transition-colors ${shares === n ? "text-white border-transparent" : "bg-white text-[#123026] border-[#EAE5DC]"}`} style={shares === n ? { backgroundColor: theme.primary } : {}}>{n}</button>
              ))}
            </div>
            <p className="text-sm text-[#123026] mt-2 font-semibold">Votre part : {eur(amount)} <span className="text-[#8A918D] font-normal">({shares} parts)</span></p>
          </div>
        )}
        {mode === "amount" && (
          <input data-testid="custom-amount-input" type="number" min="0" step="0.5" max={outstanding} value={customAmt} onChange={(e) => setCustomAmt(e.target.value)} placeholder={`Max ${eur(outstanding)}`} className="mt-2 w-full rounded-xl border border-[#EAE5DC] px-3 py-2.5 text-sm outline-none focus:border-[#123026]" />
        )}
      </div>

      {/* Tip — toujours facultatif, montant libre, 0 par défaut */}
      <div data-testid="tip-section" className="rounded-2xl bg-white border border-[#EAE5DC] p-4 mb-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-[#8A918D] mb-2 flex items-center gap-1.5"><Gift className="w-3.5 h-3.5" /> {t.tipTitle}</p>
        <div className="grid grid-cols-2 gap-2">
          <button
            data-testid="tip-none"
            onClick={() => { setTipChoice(0); setTipCustom(""); }}
            className={`rounded-xl py-2.5 text-xs font-semibold border transition-colors ${tipChoice === 0 ? "text-white border-transparent" : "bg-white text-[#123026] border-[#EAE5DC]"}`}
            style={tipChoice === 0 ? { backgroundColor: theme.primary } : {}}
          >
            {t.tipNone}
          </button>
          <button
            data-testid="tip-custom"
            onClick={() => setTipChoice(-1)}
            className={`rounded-xl py-2.5 text-xs font-semibold border transition-colors ${tipChoice === -1 ? "text-white border-transparent" : "bg-white text-[#123026] border-[#EAE5DC]"}`}
            style={tipChoice === -1 ? { backgroundColor: theme.primary } : {}}
          >
            {t.tipCustom}
          </button>
        </div>
        {tipChoice === -1 && (
          <div className="mt-2 relative">
            <input data-testid="tip-custom-input" type="number" min="0" step="0.5" value={tipCustom} onChange={(e) => setTipCustom(e.target.value)} placeholder="0,00" className="w-full rounded-xl border border-[#EAE5DC] pl-3 pr-8 py-2.5 text-sm outline-none focus:border-[#123026]" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A918D] text-sm">€</span>
          </div>
        )}
      </div>

      {/* Email receipt (optional) */}
      <div className="rounded-2xl bg-white border border-[#EAE5DC] p-4 mb-4">
        <label className="text-xs font-semibold uppercase tracking-wider text-[#8A918D] mb-2 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {t.emailLabel}</label>
        <input data-testid="receipt-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t.emailPlaceholder} className="w-full rounded-xl border border-[#EAE5DC] px-3 py-2 text-sm outline-none focus:border-[#123026]" />
      </div>

      {/* Amount to pay */}
      <div className="rounded-2xl p-4 mb-4" style={{ backgroundColor: "#EBF2EE" }}>
        {tipValue > 0 && (
          <div className="space-y-0.5 mb-2 text-sm text-[#3C6B57]">
            <div className="flex justify-between"><span>{t.billPart}</span><span>{eur(amount)}</span></div>
            <div className="flex justify-between"><span>{t.tipLine}</span><span>{eur(tipValue)}</span></div>
          </div>
        )}
        <div className="flex justify-between items-center">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#3C6B57]">{t.youWillPay}</span>
          <span data-testid="pay-amount" className="font-bold text-3xl" style={{ color: theme.primary }}>{eur(payTotal)}</span>
        </div>
      </div>

      <div className="space-y-3">
        {cashRequested && (
          <div data-testid="bill-cash-pending" className="rounded-xl bg-[#FAF3E0] border border-[#C69C4A]/50 text-[#7A5A14] text-xs font-semibold text-center px-3 py-2.5">
            {t.cashPendingNotice} · {eur(pendingCash)}
          </div>
        )}
        {(restaurant?.stripe_mode || "test") === "test" && (
          <div data-testid="bill-test-banner" className="rounded-xl bg-amber-50 border border-amber-300 text-amber-700 text-[11px] font-semibold text-center px-3 py-2">
            ⚠️ Mode TEST / Sandbox — utilisez une carte de test, aucun débit réel
          </div>
        )}
        <Button data-testid="btn-pay-phone" onClick={() => doPay("stripe")} disabled={!canPay} className="w-full h-14 rounded-full text-white font-bold text-base disabled:opacity-50" style={{ backgroundColor: theme.primary }}>
          {submitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Smartphone className="w-5 h-5 mr-2" />} {t.payByPhone}
        </Button>
        <p className="text-center text-[11px] text-[#B4B9B4] -mt-1">{t.paySecure}</p>
        <Button data-testid="btn-pay-server" onClick={() => doPay("cash")} disabled={!canPay} variant="outline" className="w-full py-3.5 rounded-full border-[#C69C4A] text-[#123026] hover:bg-[#FAF3E0] font-semibold disabled:opacity-50">
          <HandCoins className="w-5 h-5 mr-2 text-[#C69C4A]" /> {t.payAtServer}
        </Button>
        <p className="text-center text-[11px] text-[#B4B9B4]">{t.payCashHint}</p>
      </div>
      <p className="text-center text-[11px] text-[#B4B9B4] pt-8">{t.poweredBy}</p>
    </div>
  );
}

const Row = ({ label, value, className = "" }) => (
  <div className={`flex justify-between text-sm ${className}`}>
    <span className={className ? "" : "text-[#8A918D]"}>{label}</span>
    <span className="font-semibold">{value}</span>
  </div>
);
